/**
 * OAR (Office Action Response) TypeScript Type Definitions
 * Generated from Java models in telecom-core
 *
 * @see telecom-core/src/main/java/com/patsnap/drafting/enums/task/AiTaskContentTypeEnum.java
 * @see telecom-core/src/main/java/com/patsnap/drafting/enums/oar/OarParseStatusEnum.java
 * @see telecom-core/src/main/java/com/patsnap/drafting/enums/oar/PatentOfficeEnum.java
 * @see telecom-core/src/main/java/com/patsnap/drafting/model/oar/OarPdfInfoBO.java
 * @see telecom-core/src/main/java/com/patsnap/drafting/model/oar/OfficeActionHeaderBO.java
 */

/**
 * OAR 解析状态枚举
 * Java: OarParseStatusEnum
 */
export enum OarParseStatus {
    /** 未开始解析 */
    NOT_STARTED = 'NOT_STARTED',
    /** 解析中 */
    PARSING = 'PARSING',
    /** 解析成功 */
    SUCCESS = 'SUCCESS',
    /** 解析失败 */
    FAILED = 'FAILED'
}

/**
 * 审查意见问题性质枚举
 * Java: IssueNatureEnum
 */
export enum IssueNature {
    /** 实质性问题 - 涉及专利的新颖性、创造性、实用性等核心授权条件 */
    SUBSTANTIVE = 'substantive',
    /** 形式性问题 - 涉及文件格式、撰写规范、程序性要求等 */
    FORMAL = 'formal'
}

/**
 * 专利受理局枚举
 * Java: PatentOfficeEnum
 */
export enum PatentOffice {
    /** 中国国家知识产权局 */
    CNIPA = 'CNIPA',
    /** 美国专利商标局 */
    USPTO = 'USPTO',
    /** 欧洲专利局 */
    EPO = 'EPO',
    /** 日本特许厅 */
    JPO = 'JPO',
    /** 韩国特许厅 */
    KIPO = 'KIPO',
    /** 世界知识产权组织（PCT） */
    WIPO = 'WIPO',
    /** 其他 */
    OTHER = 'OTHER'
}

/**
 * OAR PDF 文件信息
 * Java: OarPdfInfoBO
 */
export interface OarPdfInfo {
    /** PDF 文件的 S3 存储键 */
    pdfS3Key: string;
    /** PDF 文件名 */
    pdfFileName: string;
    /** PDF 文件大小(字节) */
    pdfFileSize: number;
    /** 上传时间戳 */
    uploadedAt: number;
    /** PDF 文件的访问 URL */
    pdfUrl?: string;
}

/**
 * 专利代理机构信息
 * Java: OfficeActionHeaderBO.AgencyInfoBO
 */
export interface AgencyInfo {
    /** 代理机构名称 */
    name: string;
    /** 代理机构地址 */
    address: string;
    /** 联系人姓名 */
    contactPerson: string;
    /** 联系人电话 */
    phoneNumber: string;
}

/**
 * 审查意见通知书头部信息
 * Java: OfficeActionHeaderBO
 */
export interface OfficeActionHeader {
    /** 审查类型标题 */
    documentType: string;
    /** 审查机关（专利受理局） */
    authority: PatentOffice;
    /** 文件编号 */
    documentNumber: string;
    /** 发文日期 */
    issueDate: string;
    /** 专利代理机构信息 */
    agency: AgencyInfo;
    /** 申请号或专利号 */
    applicationNumber: string;
    /** 发文序号 */
    documentSequenceNumber: string;
    /** 申请人或专利权人 */
    applicant: string;
    /** 发明创造名称 */
    inventionTitle: string;
    /** 文档条形码信息 */
    barcodes: string[];
}

/**
 * 引用对比文件业务对象
 * Java: CitedDocumentBO
 */
export interface CitedDocument {
    /** 对比文件编号，例如：1、2、3 等 */
    number: string;
    /** 对比文件的文件号或名称，例如：CN123456789A、US2020/0123456 等专利号 */
    documentNumberOrName: string;
    /** 对比文件的公开日期（或抵触申请的申请日），格式：YYYY-MM-DD */
    publicationDate: string;
    /** 是否为专利文献（true: 专利文献, false: 非专利文献） */
    isPatent: boolean;
    /** 对比文献的文本段落（当对比文件是非专利文献时，包含相关文本摘录） */
    documentText: string;
}

/**
 * 审查结论性意见业务对象
 * Java: ConclusiveOpinionBO
 */
export interface ConclusiveOpinion {
    /** 专利受理局 */
    patentOffice: PatentOffice;
    /** 法条编号，例如：Art.5（第5条）、Art.26.3（第26条第3款）、Rule 20（实施细则第20条） */
    articleNumber: string;
    /** 法条完整标题/描述 */
    articleTitle: string;
    /** 问题性质（实质性问题或形式性问题） */
    issueNature: IssueNature;
    /** 是否被选中/标记（true: 审查员指出该问题存在） */
    isSelected: boolean;
    /** 是否与权利要求相关（true: 针对权利要求书的审查意见） */
    isClaimRelated: boolean;
    /** 涉及的权利要求号列表，例如：["1", "3", "5-7"] */
    affectedClaims: string[];
    /** 其他补充说明 */
    otherNotes: string;
    /** 意见ID */
    opinionId: string;
}

/**
 * 审查员结论业务对象
 * Java: ExaminerConclusionBO
 */
export interface ExaminerConclusion {
    /** 专利受理局 */
    patentOffice: PatentOffice;
    /** 结论文本 - 审查员给出的具体结论描述 */
    conclusionText: string;
}

/**
 * 审查意见通知书正文段落业务对象
 * Java: OfficeActionBodySectionBO
 */
export interface OfficeActionBodySection {
    /** 法律依据描述 - 对相关法条的详细引用和解释 */
    legalBasis: string;
    /** 涉及的权利要求号列表，例如：["1", "3", "5-7"] */
    affectedClaims: string[];
    /** 权利要求文本 - 被审查的权利要求的完整或部分文本 */
    claimText: string;
    /** 引用文献列表 - 该段落中引用的对比文件编号 */
    citedDocumentNumbers: string[];
    /** 技术特征比对分析 - 对比文件与权利要求之间的技术特征对比描述 */
    technicalComparison: string;
    /** 结论性语言 - 审查员对该问题的明确结论 */
    conclusionStatement: string;
    /** 修改建议 - 审查员给出的具体修改建议或指导 */
    amendmentSuggestion: string;
    /** 完整段落文本 - 该审查意见段落的完整原始文本 */
    fullText: string;
}

/**
 * OAR 任务内容类型定义
 * Based on AiTaskContentTypeEnum OAR section
 */
export interface OarTaskContent {
    /** OAR PDF文件信息 - CONFIG */
    oarPdfInfo?: OarPdfInfo;
    /** OAR专利受理局 - CONFIG */
    oarPatentOffice?: PatentOffice;
    /** OAR头部信息解析状态 - CONFIG */
    oarHeaderParseStatus?: OarParseStatus;
    /** OAR表格信息解析状态 - CONFIG */
    oarTableParseStatus?: OarParseStatus;
    /** OAR正文部分解析状态 - CONFIG */
    oarBodyParseStatus?: OarParseStatus;
    /** OAR审查意见通知书头部信息 - OUTPUT */
    oarHeaderInfo?: OfficeActionHeader;
    /** OAR引用文献列表 - OUTPUT */
    oarCitedDocuments?: CitedDocument[];
    /** OAR结论性意见列表 - OUTPUT */
    oarConclusiveOpinions?: ConclusiveOpinion[];
    /** OAR审查员结论列表 - OUTPUT */
    oarExaminerConclusions?: ExaminerConclusion[];
    /** OAR通知书正文文本 - OUTPUT */
    oarBodyText?: string;
    /** OAR正文段落列表 - OUTPUT */
    oarBodySections?: OfficeActionBodySection[];
}
